import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Search, RotateCcw, AlertTriangle, Trash2, Database, Plus, Edit, X, Tag, Building2 } from 'lucide-react';
import { usersAPI, serviceCallsAPI, serviceCallTypesAPI, clientsAPI, clientFlagsAPI, cspireAPI } from '@shared/apiClient';
import { FlagIcon, FLAG_ICON_OPTIONS } from '@shared/flagIcons';
import { formatDate } from '@shared/utils';
import toast from 'react-hot-toast';

// Service Call Type Form Modal Component
const ServiceCallTypeModal = ({ type, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const queryClient = useQueryClient();

  const isEdit = !!type;

  // Reset form when modal opens/closes or type changes
  useEffect(() => {
    if (isOpen) {
      if (type) {
        setFormData({
          name: type.name || '',
          description: type.description || '',
          isActive: type.isActive !== undefined ? type.isActive : true
        });
      } else {
        setFormData({
          name: '',
          description: '',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [isOpen, type]);

  const createMutation = useMutation(serviceCallTypesAPI.createServiceCallType, {
    onSuccess: () => {
      queryClient.invalidateQueries('serviceCallTypes');
      toast.success('Service call type created successfully');
      onClose();
    },
    onError: (error) => {
      const errorData = error.response?.data;
      if (errorData?.errors) {
        const fieldErrors = {};
        errorData.errors.forEach(err => {
          if (err.path) {
            fieldErrors[err.path] = err.msg;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast.error(errorData?.message || 'Failed to create service call type');
      }
    }
  });

  const updateMutation = useMutation(
    (data) => serviceCallTypesAPI.updateServiceCallType(type.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('serviceCallTypes');
        toast.success('Service call type updated successfully');
        onClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          const fieldErrors = {};
          errorData.errors.forEach(err => {
            if (err.path) {
              fieldErrors[err.path] = err.msg;
            }
          });
          setErrors(fieldErrors);
        } else {
          toast.error(errorData?.message || 'Failed to update service call type');
        }
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {isEdit ? 'Edit Service Call Type' : 'Create Service Call Type'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input ${errors.name ? 'border-red-500' : ''}`}
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={`input ${errors.description ? 'border-red-500' : ''}`}
              rows={3}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="btn btn-primary"
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// CSpire Schedule Form Modal Component
const CspireScheduleModal = ({ schedule, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    amount: '',
    effectiveDate: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const queryClient = useQueryClient();

  const isEdit = !!schedule;

  useEffect(() => {
    if (isOpen) {
      if (schedule) {
        setFormData({
          name: schedule.name || '',
          code: schedule.code || '',
          description: schedule.description || '',
          amount: schedule.amount ? String(schedule.amount) : '',
          effectiveDate: schedule.effectiveDate ? new Date(schedule.effectiveDate).toISOString().split('T')[0] : '',
          isActive: schedule.isActive !== undefined ? schedule.isActive : true
        });
      } else {
        setFormData({
          name: '',
          code: '',
          description: '',
          amount: '',
          effectiveDate: '',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [isOpen, schedule]);

  const createMutation = useMutation(cspireAPI.createPriceSchedule, {
    onSuccess: () => {
      queryClient.invalidateQueries('cspirePriceSchedules');
      toast.success('CSpire schedule created successfully');
      onClose();
    },
    onError: (error) => {
      const errorData = error.response?.data;
      if (errorData?.errors) {
        const fieldErrors = {};
        errorData.errors.forEach(err => {
          if (err.path) {
            fieldErrors[err.path] = err.msg;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast.error(errorData?.message || 'Failed to create schedule');
      }
    }
  });

  const updateMutation = useMutation(
    (data) => cspireAPI.updatePriceSchedule(schedule.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cspirePriceSchedules');
        toast.success('CSpire schedule updated successfully');
        onClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          const fieldErrors = {};
          errorData.errors.forEach(err => {
            if (err.path) {
              fieldErrors[err.path] = err.msg;
            }
          });
          setErrors(fieldErrors);
        } else {
          toast.error(errorData?.message || 'Failed to update schedule');
        }
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    const payload = {
      ...formData,
      amount: formData.amount ? parseFloat(formData.amount) : 0,
      effectiveDate: formData.effectiveDate || null
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {isEdit ? 'Edit CSpire Schedule' : 'Create CSpire Schedule'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input ${errors.name ? 'border-red-500' : ''}`}
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              className={`input ${errors.code ? 'border-red-500' : ''}`}
            />
            {errors.code && (
              <p className="mt-1 text-sm text-red-600">{errors.code}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount *
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className={`input ${errors.amount ? 'border-red-500' : ''}`}
              required
            />
            {errors.amount && (
              <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Effective Date
            </label>
            <input
              type="date"
              value={formData.effectiveDate}
              onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={`input ${errors.description ? 'border-red-500' : ''}`}
              rows={3}
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="cspireIsActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="cspireIsActive" className="ml-2 block text-sm text-gray-900">
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="btn btn-primary"
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Client Flag Form Modal Component
const ClientFlagModal = ({ flag, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'Tag',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const queryClient = useQueryClient();

  const isEdit = !!flag;

  useEffect(() => {
    if (isOpen) {
      if (flag) {
        setFormData({
          name: flag.name || '',
          description: flag.description || '',
          icon: flag.icon || 'Tag',
          isActive: flag.isActive !== undefined ? flag.isActive : true
        });
      } else {
        setFormData({
          name: '',
          description: '',
          icon: 'Tag',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [isOpen, flag]);

  const createMutation = useMutation(clientFlagsAPI.createClientFlag, {
    onSuccess: () => {
      queryClient.invalidateQueries('clientFlags');
      toast.success('Client flag created successfully');
      onClose();
    },
    onError: (error) => {
      const errorData = error.response?.data;
      if (errorData?.errors) {
        const fieldErrors = {};
        errorData.errors.forEach(err => {
          if (err.path) {
            fieldErrors[err.path] = err.msg;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast.error(errorData?.message || 'Failed to create client flag');
      }
    }
  });

  const updateMutation = useMutation(
    (data) => clientFlagsAPI.updateClientFlag(flag.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clientFlags');
        toast.success('Client flag updated successfully');
        onClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          const fieldErrors = {};
          errorData.errors.forEach(err => {
            if (err.path) {
              fieldErrors[err.path] = err.msg;
            }
          });
          setErrors(fieldErrors);
        } else {
          toast.error(errorData?.message || 'Failed to update client flag');
        }
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {isEdit ? 'Edit Client Flag' : 'Create Client Flag'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input ${errors.name ? 'border-red-500' : ''}`}
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={`input ${errors.description ? 'border-red-500' : ''}`}
              rows={3}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Icon
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {FLAG_ICON_OPTIONS.map(({ value, label, Icon }) => {
                const isSelected = formData.icon === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: value })}
                    className={`border rounded-md px-2 py-2 text-xs flex flex-col items-center gap-1 transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-blue-300 text-gray-600'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-center">{label}</span>
                  </button>
                );
              })}
            </div>
            {errors.icon && (
              <p className="mt-1 text-sm text-red-600">{errors.icon}</p>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="flagIsActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="flagIsActive" className="ml-2 block text-sm text-gray-900">
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="btn btn-primary"
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function AdvancedSettings() {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('site');
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);
  const [showCspireScheduleModal, setShowCspireScheduleModal] = useState(false);
  const [editingCspireSchedule, setEditingCspireSchedule] = useState(null);
  const [clientPage, setClientPage] = useState(1);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery(
    ['deletedUsers', page, searchTerm],
    () => usersAPI.getDeletedUsers({ page, limit: 20, search: searchTerm }),
    { keepPreviousData: true }
  );

  const { data: typesData, isLoading: typesLoading } = useQuery(
    'serviceCallTypes',
    () => serviceCallTypesAPI.getServiceCallTypes({ includeInactive: true }),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: flagsData, isLoading: flagsLoading } = useQuery(
    'clientFlags',
    () => clientFlagsAPI.getClientFlags({ includeInactive: true }),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: cspireSchedulesData, isLoading: cspireSchedulesLoading } = useQuery(
    'cspirePriceSchedules',
    () => cspireAPI.getPriceSchedules({ includeInactive: true }),
    { staleTime: 5 * 60 * 1000 }
  );

  const deleteTypeMutation = useMutation(serviceCallTypesAPI.deleteServiceCallType, {
    onSuccess: () => {
      queryClient.invalidateQueries('serviceCallTypes');
      toast.success('Service call type deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete service call type');
    }
  });

  const undeleteMutation = useMutation(usersAPI.undeleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('deletedUsers');
      queryClient.invalidateQueries('users');
      toast.success('User restored successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to restore user');
    },
  });

  const { data: inactiveClientsData, isLoading: inactiveClientsLoading } = useQuery(
    ['inactiveClients', clientPage, clientSearchTerm],
    () => clientsAPI.getClients({ page: clientPage, limit: 20, isActive: false, search: clientSearchTerm || undefined }),
    { keepPreviousData: true, enabled: activeTab === 'clients' }
  );

  const reactivateClientMutation = useMutation(clientsAPI.reactivateClient, {
    onSuccess: () => {
      queryClient.invalidateQueries('inactiveClients');
      queryClient.invalidateQueries('clients');
      toast.success('Customer reactivated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reactivate customer');
    },
  });

  const clearServiceCallsMutation = useMutation(serviceCallsAPI.deleteAllServiceCalls, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('serviceCalls');
      toast.success(`Successfully deleted ${data.data.deletedCount || 0} service call(s)`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to clear service calls');
    },
  });

  const clearCspireWorkOrdersMutation = useMutation(cspireAPI.deleteAllWorkOrders, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('cspireWorkOrders');
      const deleted = data.data.deletedCount || 0;
      const linkedDeleted = data.data.linkedServiceCallsDeleted || 0;
      let msg = `Successfully deleted ${deleted} CSpire work order(s)`;
      if (linkedDeleted > 0) {
        msg += ` and ${linkedDeleted} linked service call(s)`;
      }
      toast.success(msg);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to clear CSpire work orders');
    },
  });

  const clearGeneratorInfoMutation = useMutation(clientsAPI.clearGeneratorInfo, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('clients');
      toast.success(`Successfully cleared generator information from ${data.data.clearedCount || 0} customer(s)`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to clear generator information');
    },
  });

  const handleUndelete = (userId, userName) => {
    if (window.confirm(`Are you sure you want to restore ${userName}?`)) {
      undeleteMutation.mutate(userId);
    }
  };

  const handleReactivateClient = (clientId, companyName) => {
    if (window.confirm(`Are you sure you want to reactivate ${companyName}?`)) {
      reactivateClientMutation.mutate(clientId);
    }
  };

  const handleClearServiceCalls = () => {
    const confirmMessage = `WARNING: This will permanently delete ALL service calls from the database.\n\nThis action cannot be undone. Are you absolutely sure you want to continue?`;
    
    if (window.confirm(confirmMessage)) {
      // Double confirmation for destructive action
      if (window.confirm('This is your final warning. Click OK to permanently delete all service calls.')) {
        clearServiceCallsMutation.mutate();
      }
    }
  };

  const handleClearCspireWorkOrders = () => {
    const confirmMessage = `WARNING: This will permanently delete ALL CSpire work orders and their linked service calls from the database.\n\nThis action cannot be undone. Are you absolutely sure you want to continue?`;
    
    if (window.confirm(confirmMessage)) {
      if (window.confirm('This is your final warning. Click OK to permanently delete all CSpire work orders.')) {
        clearCspireWorkOrdersMutation.mutate();
      }
    }
  };

  const handleClearGeneratorInfo = () => {
    const confirmMessage = `WARNING: This will clear generator type, model, serial number, and size from ALL clients.\n\nFilter and oil information will be preserved. This action cannot be undone.\n\nAre you sure you want to continue?`;
    
    if (window.confirm(confirmMessage)) {
      clearGeneratorInfoMutation.mutate();
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setShowTypeModal(true);
  };

  const handleCreateType = () => {
    setEditingType(null);
    setShowTypeModal(true);
  };

  const handleDeleteType = (type) => {
    if (window.confirm(`Are you sure you want to delete "${type.name}"? This action cannot be undone if any service calls are using this type.`)) {
      deleteTypeMutation.mutate(type.id);
    }
  };

  const deleteFlagMutation = useMutation(clientFlagsAPI.deleteClientFlag, {
    onSuccess: () => {
      queryClient.invalidateQueries('clientFlags');
      toast.success('Client flag deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete client flag');
    }
  });

  const deleteCspireScheduleMutation = useMutation(cspireAPI.deletePriceSchedule, {
    onSuccess: () => {
      queryClient.invalidateQueries('cspirePriceSchedules');
      toast.success('CSpire schedule deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete CSpire schedule');
    }
  });

  const handleEditFlag = (flag) => {
    setEditingFlag(flag);
    setShowFlagModal(true);
  };

  const handleCreateFlag = () => {
    setEditingFlag(null);
    setShowFlagModal(true);
  };

  const handleDeleteFlag = (flag) => {
    if (window.confirm(`Are you sure you want to delete "${flag.name}"? This action cannot be undone if any customers are using this flag.`)) {
      deleteFlagMutation.mutate(flag.id);
    }
  };

  const handleCreateCspireSchedule = () => {
    setEditingCspireSchedule(null);
    setShowCspireScheduleModal(true);
  };

  const handleEditCspireSchedule = (schedule) => {
    setEditingCspireSchedule(schedule);
    setShowCspireScheduleModal(true);
  };

  const handleDeleteCspireSchedule = (schedule) => {
    if (window.confirm(`Are you sure you want to delete "${schedule.name}"? This action cannot be undone.`)) {
      deleteCspireScheduleMutation.mutate(schedule.id);
    }
  };

  const serviceCallTypes = typesData?.data?.types || [];
  const clientFlags = flagsData?.data?.flags || [];
  const cspireSchedules = cspireSchedulesData?.data?.schedules || [];
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Advanced Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage site settings, users, and system maintenance tools.
          </p>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-6">
          <button
            onClick={() => setActiveTab('site')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'site'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Site Settings
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'clients'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Clients
          </button>
          <button
            onClick={() => setActiveTab('advanced')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'advanced'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Advanced
          </button>
        </nav>
      </div>

      {activeTab === 'site' && (
        <>
          {/* Service Call Types Management Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <Tag className="h-5 w-5 text-gray-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Service Call Types</h2>
                </div>
                <p className="text-sm text-gray-600">
                  Manage service call types that can be assigned to service calls.
                </p>
              </div>
              <button
                onClick={handleCreateType}
                className="btn btn-primary flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Type</span>
              </button>
            </div>

            {typesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {serviceCallTypes.length > 0 ? (
                      serviceCallTypes.map((type) => (
                        <tr key={type.id}>
                          <td className="font-medium">{type.name}</td>
                          <td className="text-gray-600">{type.description || '-'}</td>
                          <td>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              type.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {type.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditType(type)}
                                className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                              >
                                <Edit className="h-4 w-4" />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleDeleteType(type)}
                                className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                                disabled={deleteTypeMutation.isLoading}
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-8 text-gray-500">
                          No service call types found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* CSpire Price Schedules Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <Tag className="h-5 w-5 text-gray-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">CSpire Price Schedules</h2>
                </div>
                <p className="text-sm text-gray-600">
                  Manage pricing schedules used for CSpire work orders.
                </p>
              </div>
              <button
                onClick={handleCreateCspireSchedule}
                className="btn btn-primary flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Schedule</span>
              </button>
            </div>

            {cspireSchedulesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Code</th>
                      <th>Amount</th>
                      <th>Effective Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {cspireSchedules.length > 0 ? (
                      cspireSchedules.map((schedule) => (
                        <tr key={schedule.id}>
                          <td className="font-medium">{schedule.name}</td>
                          <td className="text-gray-600">{schedule.code || '-'}</td>
                          <td className="text-gray-600">
                            {Number(schedule.amount || 0).toLocaleString('en-US', {
                              style: 'currency',
                              currency: 'USD'
                            })}
                          </td>
                          <td className="text-gray-600">
                            {schedule.effectiveDate ? formatDate(schedule.effectiveDate) : '-'}
                          </td>
                          <td>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              schedule.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {schedule.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditCspireSchedule(schedule)}
                                className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                              >
                                <Edit className="h-4 w-4" />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleDeleteCspireSchedule(schedule)}
                                className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                                disabled={deleteCspireScheduleMutation.isLoading}
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-gray-500">
                          No CSpire schedules found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Client Flags Management Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <Tag className="h-5 w-5 text-gray-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Client Flags</h2>
                </div>
                <p className="text-sm text-gray-600">
                  Manage icons that can be applied to customers.
                </p>
              </div>
              <button
                onClick={handleCreateFlag}
                className="btn btn-primary flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Flag</span>
              </button>
            </div>

            {flagsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Icon</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {clientFlags.length > 0 ? (
                      clientFlags.map((flag) => (
                        <tr key={flag.id}>
                          <td>
                            <FlagIcon
                              iconName={flag.icon}
                              className={`h-4 w-4 ${flag.isActive ? 'text-gray-700' : 'text-gray-400'}`}
                              title={flag.name}
                            />
                          </td>
                          <td className="font-medium">{flag.name}</td>
                          <td className="text-gray-600">{flag.description || '-'}</td>
                          <td>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              flag.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {flag.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditFlag(flag)}
                                className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                              >
                                <Edit className="h-4 w-4" />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleDeleteFlag(flag)}
                                className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                                disabled={deleteFlagMutation.isLoading}
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-8 text-gray-500">
                          No client flags found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'users' && (
        <>
          {/* Info Alert */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Deleted Users</h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Users marked as deleted are hidden from all views. You can restore them here if needed.
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search deleted users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
          </div>

          {/* Deleted Users Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Deleted Date</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.data?.users && data.data.users.length > 0 ? (
                    data.data.users.map((user) => (
                      <tr key={user.id}>
                        <td className="font-medium">
                          {user.firstName} {user.lastName}
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className="capitalize">{user.role}</span>
                        </td>
                        <td>{formatDate(user.updatedAt)}</td>
                        <td>{formatDate(user.createdAt)}</td>
                        <td>
                          <button
                            onClick={() => handleUndelete(user.id, `${user.firstName} ${user.lastName}`)}
                            className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                            disabled={undeleteMutation.isLoading}
                          >
                            <RotateCcw className="h-4 w-4" />
                            <span>Restore</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-gray-500">
                        {error ? (
                          <div>
                            <p>Error loading deleted users</p>
                            <p className="text-xs text-red-500 mt-1">{error.message}</p>
                          </div>
                        ) : (
                          'No deleted users found'
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {data?.data?.pagination && data.data.pagination.total > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {data.data.pagination.start} to {data.data.pagination.end} of{' '}
                {data.data.pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={!data.data.pagination.hasPrev}
                  className="btn btn-secondary btn-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={!data.data.pagination.hasNext}
                  className="btn btn-secondary btn-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'clients' && (
        <>
          {/* Info Alert */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Deactivated Customers</h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Customers marked as deactivated are hidden from normal views. You can restore them here if needed.
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search deactivated customers..."
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
          </div>

          {/* Deactivated Clients Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inactiveClientsLoading ? (
                    <tr>
                      <td colSpan="4" className="text-center py-8">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : inactiveClientsData?.data?.clients?.length > 0 ? (
                    inactiveClientsData.data.clients.map((client) => (
                      <tr key={client.id}>
                        <td className="font-medium">{client.companyName}</td>
                        <td className="text-gray-600">
                          {[client.contactFirstName, client.contactLastName].filter(Boolean).join(' ') || '-'}
                        </td>
                        <td className="text-gray-600">{client.email || '-'}</td>
                        <td>
                          <button
                            onClick={() => handleReactivateClient(client.id, client.companyName)}
                            className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                            disabled={reactivateClientMutation.isLoading}
                          >
                            <RotateCcw className="h-4 w-4" />
                            <span>Reactivate</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-8 text-gray-500">
                        No deactivated customers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {inactiveClientsData?.data?.pagination && inactiveClientsData.data.pagination.total > 0 && (() => {
            const pagination = inactiveClientsData.data.pagination;
            const start = (pagination.page - 1) * pagination.limit + 1;
            const end = Math.min(pagination.page * pagination.limit, pagination.total);
            const hasPrev = pagination.page > 1;
            const hasNext = pagination.page < pagination.pages;
            return (
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-700">
                  Showing {start} to {end} of {pagination.total} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setClientPage(clientPage - 1)}
                    disabled={!hasPrev}
                    className="btn btn-secondary btn-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setClientPage(clientPage + 1)}
                    disabled={!hasNext}
                    className="btn btn-secondary btn-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {activeTab === 'advanced' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Database className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Database Management</h2>
          </div>
          
          {/* Clear Service Calls */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-base font-medium text-gray-900 mb-1">Clear Service Calls Database</h3>
                <p className="text-sm text-gray-600">
                  Clear service call database to start fresh. This action is permanent and cannot be undone.
                </p>
              </div>
              <button
                onClick={handleClearServiceCalls}
                disabled={clearServiceCallsMutation.isLoading}
                className="btn btn-danger flex items-center space-x-2"
              >
                {clearServiceCallsMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Clearing...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Clear Service Calls Database</span>
                  </>
                )}
              </button>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-red-800">Warning</h4>
                  <p className="mt-1 text-sm text-red-700">
                    This will permanently delete all service calls from the database. This action cannot be undone.
                    Make sure you have a backup if you need to recover this data later.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Clear CSpire Work Orders */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-base font-medium text-gray-900 mb-1">Clear CSpire Work Orders</h3>
                <p className="text-sm text-gray-600">
                  Delete all CSpire work orders and their linked service calls. This action is permanent and cannot be undone.
                </p>
              </div>
              <button
                onClick={handleClearCspireWorkOrders}
                disabled={clearCspireWorkOrdersMutation.isLoading}
                className="btn btn-danger flex items-center space-x-2"
              >
                {clearCspireWorkOrdersMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Clearing...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Clear CSpire Work Orders</span>
                  </>
                )}
              </button>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-red-800">Warning</h4>
                  <p className="mt-1 text-sm text-red-700">
                    This will permanently delete all CSpire work orders and any service calls linked to them.
                    This action cannot be undone. Make sure you have a backup if you need to recover this data later.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Clear Generator Information */}
          <div>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-base font-medium text-gray-900 mb-1">Clear Generator Information</h3>
                <p className="text-sm text-gray-600">
                  Clear generator type, model, serial number, and size from all clients. Filter and oil information will be preserved.
                </p>
              </div>
              <button
                onClick={handleClearGeneratorInfo}
                disabled={clearGeneratorInfoMutation.isLoading}
                className="btn btn-danger flex items-center space-x-2"
              >
                {clearGeneratorInfoMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Clearing...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Clear Generator Info</span>
                  </>
                )}
              </button>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">Information</h4>
                  <p className="mt-1 text-sm text-yellow-700">
                    This will clear generator type, model, serial number, and size from all clients. 
                    Filter information (air, fuel, oil filters) and oil information (type, amount, fuel additive) will be preserved.
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Call Type Modal */}
      <ServiceCallTypeModal
        type={editingType}
        isOpen={showTypeModal}
        onClose={() => {
          setShowTypeModal(false);
          setEditingType(null);
        }}
      />

      {/* CSpire Schedule Modal */}
      <CspireScheduleModal
        schedule={editingCspireSchedule}
        isOpen={showCspireScheduleModal}
        onClose={() => {
          setShowCspireScheduleModal(false);
          setEditingCspireSchedule(null);
        }}
      />

      {/* Client Flag Modal */}
      <ClientFlagModal
        flag={editingFlag}
        isOpen={showFlagModal}
        onClose={() => {
          setShowFlagModal(false);
          setEditingFlag(null);
        }}
      />
    </div>
  );
}
